import './assets/css/bootstrap.min.css';
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

import { Canvas } from '@react-three/fiber';
import SidePopup from './components/SidePopup';
import './assets/css/main.css';
import Menu from './components/Menu';
import { extend } from '@react-three/fiber';
// side Pages
import Connect from './SideAreas/Connect';
import About from './SideAreas/About';
import Portfolio from './SideAreas/Portfolio';
import Services from './SideAreas/Services';
import SinglePortfolio from './SideAreas/SinglePortfolio';
import MainContext from './store/main-context';
import { Scene } from './Scene';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import IntroCrawl from './components/IntroCrawl';
import { generateStars } from './services/starfield';
import StarsField from './components/StarsField';
import { getSeoMeta, getStructuredData } from './services/seo';
import {
  getPathForSidebar,
  getPortfolioSlugFromPathname,
  getSidebarFromPathname,
  getVisualPathname,
  INTRO_ANIMATION_COMPLETE_MS,
  INTRO_CRAWL_SWAP_DELAY_MS,
  INTRO_TEXT_DELAY_MS,
  isSubRoutePath,
  ITEMS_FADE_IN_DELAY_MS,
  normalizePathname,
  shouldSkipInitialDeepLinkModelAnimation,
  SIDEBAR_CLOSE_ANIMATION_MS,
  type ResetIntroOptions,
} from './services/app-flow';
import type { PortfolioItem, SeoSidebar } from './store/types';

extend({ OrbitControls });

const DESKTOP_STARS_COUNT = 400;
const MOBILE_STARS_COUNT = 520;
const MOBILE_BREAKPOINT = 992;

function App() {
  const initialPathname = useMemo(() => normalizePathname(window.location.pathname), []);
  const [didEnterViaSubRoute] = useState<boolean>(() => isSubRoutePath(initialPathname));
  const initialDeepLinkSidebar = useMemo<SeoSidebar>(() => getSidebarFromPathname(initialPathname), [initialPathname]);

  const [fadeOutIntroText, setFadeOutIntroText] = useState<boolean>(false);
  const [fadeOutCrawlText, setFadeOutCrawlText] = useState<boolean>(true);

  const [modelsLoaded, setModelsLoaded] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const [introAnimationLoaded, setIntroAnimationLoaded] = useState(false);

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [fadeInItems, setFadeInItems] = useState<boolean>(didEnterViaSubRoute);
  const [pathname, setPathname] = useState<string>(initialPathname);
  const [introCycle, setIntroCycle] = useState<number>(0);
  const [sceneResetKey, setSceneResetKey] = useState<number>(0);
  const [useVisibleZoom, setUseVisibleZoom] = useState<boolean>(false);
  const [deferStartupRoute, setDeferStartupRoute] = useState<boolean>(didEnterViaSubRoute);
  const [shouldResetAfterFirstDeepClose, setShouldResetAfterFirstDeepClose] = useState<boolean>(didEnterViaSubRoute);
  const [deepLinkSidebarReady, setDeepLinkSidebarReady] = useState<boolean>(() => !didEnterViaSubRoute);
  const closeResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ctx = useContext(MainContext);
  const { portfolio, activePortfolio, setActivePortfolio } = ctx;

  const resetToIntroSequence = useCallback((options: ResetIntroOptions = {}) => {
    const { keepCanvasVisible = false, showCrawlImmediately = false, useVisibleZoom: shouldUseVisibleZoom = false } = options;

    setFadeOutIntroText(showCrawlImmediately);
    setFadeOutCrawlText(showCrawlImmediately ? false : true);
    setIsLoading(true);
    setFadeInItems(keepCanvasVisible);
    setUseVisibleZoom(shouldUseVisibleZoom);
    setIntroAnimationLoaded(false);
    setMenuOpen(false);
    setDeferStartupRoute(false);
    setSceneResetKey((prev) => prev + 1);
    setIntroCycle((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!didEnterViaSubRoute) {
      setDeepLinkSidebarReady(true);
      return;
    }

    const deepLinkReadyTimer = setTimeout(() => {
      setDeepLinkSidebarReady(true);
    }, INTRO_TEXT_DELAY_MS);

    return () => {
      clearTimeout(deepLinkReadyTimer);
    };
  }, [didEnterViaSubRoute]);

  useEffect(() => {
    let crawlSwapTimer: ReturnType<typeof setTimeout> | null = null;
    const introTextTimer = setTimeout(() => {
      setFadeOutIntroText(true);
      crawlSwapTimer = setTimeout(() => {
        setFadeOutCrawlText(false);
      }, INTRO_CRAWL_SWAP_DELAY_MS);
    }, INTRO_TEXT_DELAY_MS);

    const introDoneTimer = setTimeout(() => {
      setIntroAnimationLoaded(true);
    }, INTRO_ANIMATION_COMPLETE_MS);

    return () => {
      clearTimeout(introTextTimer);
      clearTimeout(introDoneTimer);
      if (crawlSwapTimer) {
        clearTimeout(crawlSwapTimer);
      }
    };
  }, [introCycle]);

  useEffect(() => {
    if (modelsLoaded && isLoading && introAnimationLoaded) {
      setFadeOutIntroText(true);
      setIsLoading(false);

      setTimeout(() => {
        setFadeInItems(true);
      }, ITEMS_FADE_IN_DELAY_MS);
    } else if (introAnimationLoaded && fadeOutIntroText && fadeOutCrawlText) {
      setFadeOutIntroText(false);
    }
  }, [modelsLoaded, fadeOutIntroText, isLoading, introAnimationLoaded, fadeOutCrawlText]);

  const [starsCount, setStarsCount] = useState<number>(() => {
    return window.innerWidth <= MOBILE_BREAKPOINT ? MOBILE_STARS_COUNT : DESKTOP_STARS_COUNT;
  });

  useEffect(() => {
    const updateStarsCount = () => {
      const nextCount = window.innerWidth <= MOBILE_BREAKPOINT ? MOBILE_STARS_COUNT : DESKTOP_STARS_COUNT;
      setStarsCount(nextCount);
    };

    window.addEventListener('resize', updateStarsCount);
    return () => {
      window.removeEventListener('resize', updateStarsCount);
    };
  }, []);

  const stars = useMemo(() => generateStars(starsCount), [starsCount]);

  useEffect(() => {
    const handlePopState = () => {
      setPathname(normalizePathname(window.location.pathname));
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const navigateTo = useCallback((path: string, replace = false) => {
    const nextPath = normalizePathname(path);
    const currentPath = normalizePathname(window.location.pathname);

    if (nextPath !== currentPath) {
      if (replace) {
        window.history.replaceState({}, '', nextPath);
      } else {
        window.history.pushState({}, '', nextPath);
      }
    }

    setPathname(nextPath);
  }, []);

  const visualPathname = useMemo(() => {
    return getVisualPathname({
      didEnterViaSubRoute,
      deepLinkSidebarReady,
      deferStartupRoute,
      pathname,
    });
  }, [deferStartupRoute, deepLinkSidebarReady, didEnterViaSubRoute, pathname]);

  const activeSidebar = useMemo<SeoSidebar>(() => getSidebarFromPathname(visualPathname), [visualPathname]);
  const projectSlug = useMemo(() => getPortfolioSlugFromPathname(visualPathname), [visualPathname]);
  const routeSidebar = useMemo<SeoSidebar>(() => getSidebarFromPathname(pathname), [pathname]);
  const routeProjectSlug = useMemo(() => getPortfolioSlugFromPathname(pathname), [pathname]);
  const skipInitialDeepLinkModelAnimation = useMemo(() => {
    return shouldSkipInitialDeepLinkModelAnimation({
      didEnterViaSubRoute,
      deepLinkSidebarReady,
      shouldResetAfterFirstDeepClose,
      activeSidebar,
      initialDeepLinkSidebar,
    });
  }, [
    activeSidebar,
    deepLinkSidebarReady,
    didEnterViaSubRoute,
    initialDeepLinkSidebar,
    shouldResetAfterFirstDeepClose,
  ]);
  const hideIntroCrawlText = didEnterViaSubRoute && shouldResetAfterFirstDeepClose;
  const hideMenuInDeepLinkFlow = didEnterViaSubRoute && shouldResetAfterFirstDeepClose;

  useEffect(() => {
    if (deferStartupRoute && fadeInItems) {
      setDeferStartupRoute(false);
    }
  }, [deferStartupRoute, fadeInItems]);

  const closeSidebar = useCallback(() => {
    navigateTo('/');
    if (!shouldResetAfterFirstDeepClose) return;

    setShouldResetAfterFirstDeepClose(false);
    setFadeOutIntroText(true);
    setFadeOutCrawlText(false);

    if (closeResetTimeoutRef.current) {
      clearTimeout(closeResetTimeoutRef.current);
      closeResetTimeoutRef.current = null;
    }

    closeResetTimeoutRef.current = setTimeout(() => {
      resetToIntroSequence({ keepCanvasVisible: true, showCrawlImmediately: true, useVisibleZoom: true });
      closeResetTimeoutRef.current = null;
    }, SIDEBAR_CLOSE_ANIMATION_MS);
  }, [navigateTo, resetToIntroSequence, shouldResetAfterFirstDeepClose]);

  const closePortfolioPopup = useCallback(() => {
    navigateTo('/my-portfolio');
  }, [navigateTo]);

  const openSidebar = useCallback((sidebar: SeoSidebar) => {
    navigateTo(getPathForSidebar(sidebar));
  }, [navigateTo]);

  useEffect(() => {
    if (!projectSlug) {
      if (activePortfolio !== null) {
        setActivePortfolio(null);
      }
      return;
    }

    const projectIndex = portfolio.findIndex((item) => item.key === projectSlug);
    setActivePortfolio(projectIndex >= 0 ? projectIndex : null);
  }, [activePortfolio, portfolio, projectSlug, setActivePortfolio]);

  const activeProject = useMemo(() => {
    if (!projectSlug) return null;
    return portfolio.find((item) => item.key === projectSlug) ?? null;
  }, [portfolio, projectSlug]);

  const showPortfolioPopup = activeSidebar === 'portfolio' && Boolean(projectSlug) && Boolean(activeProject);
  const shouldUseProjectSeo = showPortfolioPopup;

  useEffect(() => {
    if (activeSidebar === 'portfolio' && projectSlug && !activeProject) {
      navigateTo('/my-portfolio', true);
    }
  }, [activeProject, activeSidebar, navigateTo, projectSlug]);

  useEffect(() => {
    return () => {
      if (closeResetTimeoutRef.current) {
        clearTimeout(closeResetTimeoutRef.current);
      }
    };
  }, []);

  const routeActiveProject = useMemo(() => {
    if (!routeProjectSlug) return null;
    return portfolio.find((item) => item.key === routeProjectSlug) ?? null;
  }, [portfolio, routeProjectSlug]);


  const seoMeta = useMemo(() => {
    return getSeoMeta({
      activeSidebar: routeSidebar,
      showPortfolioPopup: shouldUseProjectSeo,
      activeProject: routeActiveProject,
    });
  }, [routeActiveProject, routeSidebar, shouldUseProjectSeo]);

  const activeStructuredData = useMemo(() => {
    return getStructuredData({
      activeSidebar: routeSidebar,
      showPortfolioPopup: shouldUseProjectSeo,
      activeProject: routeActiveProject,
      portfolioItems: portfolio,
      seoMeta,
    });
  }, [portfolio, routeActiveProject, routeSidebar, seoMeta, shouldUseProjectSeo]);

  return (
    <>
      <Helmet key={seoMeta.canonical} defer={false} prioritizeSeoTags>
        <title>{seoMeta.title}</title>
        <meta name="description" content={seoMeta.description} />
        <meta name="keywords" content={seoMeta.keywords} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={seoMeta.canonical} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoMeta.title} />
        <meta property="og:description" content={seoMeta.description} />
        <meta property="og:url" content={seoMeta.canonical} />
        <meta property="og:image" content={seoMeta.image} />
        <meta property="og:site_name" content="Serouj Khanjihanian" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoMeta.title} />
        <meta name="twitter:description" content={seoMeta.description} />
        <meta name="twitter:image" content={seoMeta.image} />
        {activeStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(activeStructuredData)}
          </script>
        )}
      </Helmet>

      <div className="bg-linear linear-default" />

      {!hideMenuInDeepLinkFlow && (
        <div className="fade_transition">
          <Menu
            setActiveSidebar={openSidebar}
            setMenuOpen={setMenuOpen}
            menuOpen={menuOpen}
            activeSidebar={activeSidebar}
          />
        </div>
      )}

      <IntroCrawl
        fadeOutIntroText={fadeOutIntroText}
        fadeOutCrawlText={fadeOutCrawlText}
        hideIntroCrawlText={hideIntroCrawlText}
      />
      <StarsField stars={stars} />

      <div className={' canvas_wrrapper fade_transition' + (!fadeInItems ? ' hide ' : '')}>
        <Canvas key={sceneResetKey}>
          <Scene
            setModelsLoaded={setModelsLoaded}
            isLoading={isLoading}
            activeSidebar={activeSidebar}
            setActiveSidebar={openSidebar}
            skipInitialDeepLinkModelAnimation={skipInitialDeepLinkModelAnimation}
            useVisibleZoom={useVisibleZoom}
          />
        </Canvas>
      </div>

      <SidePopup screenName="connect" closeSidebar={closeSidebar} active={activeSidebar === 'connect' ? true : false}>
        <Connect isActive={activeSidebar === 'connect'} />
      </SidePopup>

      <SidePopup closeSidebar={closeSidebar} screenName="about" active={activeSidebar === 'about' ? true : false}>
        <About />
      </SidePopup>

      <SidePopup closeSidebar={closeSidebar} screenName="services" active={activeSidebar === 'services' ? true : false}>
        <Services />
      </SidePopup>

      <SidePopup
        closeSidebar={closeSidebar}
        screenName='portfolio'
        active={activeSidebar === 'portfolio' ? true : false}
      >
        <Portfolio
          openProject={(project: PortfolioItem, projectIndex: number) => {
            setActivePortfolio(projectIndex);
            navigateTo(`/my-portfolio/${project.key}`);
          }}
        />
      </SidePopup>

      <SinglePortfolio
        closePopup={closePortfolioPopup}
        showPopup={showPortfolioPopup}
      />
    </>
  );
}
export default App;
